import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmMenuService } from '../../../../shared/services/confirm-menu/confirm-menu.service';
import { ChunkService } from '../chunk.service';

@Component({
  selector: 'app-tag-creator',
  templateUrl: './tag-creator.component.html',
  styleUrls: ['./tag-creator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatFormFieldModule, MatChipsModule, MatIconModule, MatButtonModule, ReactiveFormsModule, MatInputModule, MatDialogModule],
})
export class TagCreatorComponent {
  private fb = inject(FormBuilder);
  private confirmMenuService = inject(ConfirmMenuService);
  private translate = inject(TranslateService);
  private chunkService = inject(ChunkService);

  public dialogRef = inject(MatDialogRef<TagCreatorComponent>, { optional: true });
  private data = inject(MAT_DIALOG_DATA, { optional: true });

  chunkInput = input<any>(undefined, { alias: 'chunk' });
  courseIDInput = input<string | undefined>(undefined, { alias: 'courseID' });
  blogIDInput = input<string | undefined>(undefined, { alias: 'blogID' });

  currentChunk = signal<any>(null);
  courseID = signal<string | undefined>(undefined);
  blogID = signal<string | undefined>(undefined);

  tagForm = this.fb.nonNullable.group({
    tag: ['', Validators.required],
    language: [this.translate.currentLang, Validators.required],
    timestamp: ['', Validators.pattern(/^$|^(?:(?:([01]?\d|2[0-3]):)([0-5]?\d):)([0-5]?\d)$/)],
  });

  languages = [
    { value: 'fr', viewValue: 'Français', icon: 'flag-icon flag-icon-fr' },
    { value: 'en', viewValue: 'English', icon: 'flag-icon flag-icon-us' },
  ];

  constructor() {
    if (this.data) {
      this.currentChunk.set(this.data.chunk);
      this.courseID.set(this.data.courseID);
      this.blogID.set(this.data.blogID);
    }

    effect(() => {
      const c = this.chunkInput();
      if (c) this.currentChunk.set(c);
    });

    effect(() => {
      const c = this.courseIDInput();
      if (c) this.courseID.set(c);
    });

    effect(() => {
      const b = this.blogIDInput();
      if (b) this.blogID.set(b);
    });
  }

  addTag() {
    if (this.tagForm.invalid) return;

    const formValue = this.tagForm.getRawValue();
    const data = {
      chunkID: this.currentChunk()?._id,
      tag: formValue.tag,
      language: formValue.language,
      timestamp: formValue.timestamp,
      courseID: this.courseID(),
      blogID: this.blogID(),
      generateEmbeddingsForTags: localStorage.getItem('generateEmbeddingsTags') == 'true',
    };

    this.chunkService.addTag(data).subscribe((chunk) => {
      this.currentChunk.set(chunk);
      this.chunkService.passChunkCreatedOnCacheEvent(chunk);
      this.tagForm.controls.tag.reset();
    });
  }

  removeTag(tag: any) {
    const data = {
      chunkID: this.currentChunk()?._id,
      tag,
    };
    this.chunkService.removeTag(data).subscribe((chunk) => {
      this.currentChunk.set(chunk);
      this.chunkService.passChunkCreatedOnCacheEvent(chunk);
    });
  }

  removeAllTags(event: MouseEvent) {
    this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.AreYouSure', 'delete', 'warn').subscribe(() => {
      const data = { chunkID: this.currentChunk()?._id };
      this.chunkService.removeAllTags(data).subscribe((chunk) => {
        this.currentChunk.set(chunk);
        this.chunkService.passChunkCreatedOnCacheEvent(chunk);
      });
    });
  }
}
